import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import flow from 'lodash/flow';
import {
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import {
  fetchFeaturedCollections,
  setSelectedCollections,
  setSelectedAllCollections,
} from './actions/Collections';
import {
  setSelectedFiles,
  setSelectedAllFiles,
  fetchFileStatuses,
} from './actions/Files';
import {
  fetchTaxonomies,
  fetchMetadataGroups,
  fetchCustomFields,
} from './actions/Metadata';

import { isLoggedIn } from './api/Auth';

import Sidebar from './components/Sidebar';
import Toolbar from './components/Toolbar';
import Actionbar from './components/Actionbar';
import ConfirmationModal from './components/ConfirmationModal';
import FlashMessages from './components/FlashMessages';
import LoadingMain from './components/LoadingMain';
import UploadDropOverlay from './components/UploadDropOverlay';
import AccountUsageInfo from './components/AccountUsageInfo';
import EventHandler from './components/EventHandler';
import NavigationBlocker from './components/NavigationBlocker';
import TermsModalHandler from './components/TermsModalHandler';

import Dashboard from './containers/DashboardV2';
import File from './containers/File';
import Files from './containers/Files';
import Collections from './containers/Collections';
import DownloadRequests from './containers/DownloadRequests';
import DownloadRequestEdit from './containers/DownloadRequestEdit';
import Downloads from './containers/Downloads';
import Uploads from './containers/Uploads';
import Shares from './containers/Shares';
import ShareDetails from './containers/ShareDetails';
import Connect from './containers/Connect';
import IntegrationAuthCallback from './containers/IntegrationAuthCallback';
import NoMatch from './containers/NoMatch';
import Page from './containers/Page';
import PageInternal from './containers/PageInternal';
import Notifications from './containers/Notifications';
import UserProfile from './containers/UserProfile';
import Integrations from './containers/Integrations';
import People from './containers/People';

import AdminPages from './containers/Admin/Pages';
import AdminPageEdit from './containers/Admin/PageEdit';
import AdminPageDetails from './containers/Admin/PageDetails';
import AdminSettings from './containers/Admin/Settings';
import AdminReports from './containers/Admin/Reports';
import AdminReport from './containers/Admin/Report';
import AdminPaymentInfo from './containers/Admin/PaymentInfo';
import AdminDuplicates from './containers/Admin/Duplicates';
import AdminFTPAccounts from './containers/Admin/FTPAccounts';
import AdminFTPQueue from './containers/Admin/FTPQueue';
import AdminUploadPages from './containers/Admin/UploadPages';
import AdminUploadPageDetails from './containers/Admin/UploadPageDetails';
import SearchResults from './containers/SearchResults/SearchResults';
import SyncedCollections from './containers/SyncedCollections';

import ProfileEdit from './containers/ProfileEdit';

import {
  updateIntercomUser,
  pingIntercom,
  checkIntercomTour,
  showIntercomWidget,
  hideIntercomWidget,
  setPostHogUser,
  isImportModalOpen,
  uuidPattern,
} from './helpers';
import {
  isArchiveUrl,
  isArchive,
  resizeSplitContentContainer,
  resizeAccountUsageInfo,
  accountUsageLock,
  subscriptionExpired,
  shouldShowSecondSidebar,
} from './helpers/App';
import {
  userCan,
  userHasCap,
  userIsAdmin,
  updateUsedWorkspacesCookie,
} from './helpers/Auth';
import { uploadFilesTarget } from './helpers/DNDUploadFiles';
import { userHasFeature } from './helpers/Features';
import { getSetting } from './helpers/Settings';
import { showModal } from './actions';

class AppContent extends Component {
  constructor(props) {
    super(props);

    this.previousLocation = props.location;

    this.fileWithLocation = this.fileWithLocation.bind(this);
    this.handleTrialMessage = this.handleTrialMessage.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', resizeSplitContentContainer);
    window.addEventListener('resize', resizeAccountUsageInfo);

    const {
      loadFeaturedCollections,
      loadMetadataGroups,
      loadTaxonomies,
      loadCustomFields,
      loadFileStatuses,
    } = this.props;

    if (!isLoggedIn()) {
      return;
    }

    checkIntercomTour();

    if (!isArchive()) {
      loadFeaturedCollections();
    }
    loadMetadataGroups();
    loadTaxonomies();
    loadCustomFields();
    loadFileStatuses();

    updateUsedWorkspacesCookie();
  }

  shouldComponentUpdate(nextProps) {
    const {
      history,
      profile,
    } = this.props;

    if (profile) {
      if (profile.client) {
        if (subscriptionExpired(profile.client) || profile.client.usage_lock) {
          const path = userIsAdmin() ? '/admin/settings/subscription' : '/subscription';

          if (nextProps.location.pathname !== path) {
            history.push(path);
            return false;
          }
        }
      }

      if (!profile.g2fa_enabled && getSetting('2fa_required')) {
        if (
          nextProps.location.pathname.replace(/\/$/, '') !== '/profile/edit' &&
          nextProps.location.pathname.replace(/\/$/, '') !== '/subscription' &&
          nextProps.location.pathname.replace(/\/$/, '') !== '/admin/settings/subscription'
        ) {
          history.push('/profile/edit/?tab=2fa');
          return false;
        }
      }
    }

    return true;
  }

  componentDidUpdate(prevProps) {
    const {
      clearSelectedFiles,
      clearSelectedCollections,
      location,
      loadFeaturedCollections,
      selectedFiles,
      profile,
      uploadModal,
    } = this.props;
    const { profile: prevProfile } = prevProps;

    this.setSupportWidgetVisibility();
    if (profile?.uuid !== prevProfile?.uuid && !uploadModal && !isImportModalOpen()) {
      updateIntercomUser(profile);
      setPostHogUser(profile);
      this.handleTrialMessage();
    }
    if (prevProps.location.key !== location.key && !isImportModalOpen()) {
      pingIntercom();
    }
    if (prevProps.location.search !== location.search) {
      checkIntercomTour();
    }

    this.checkAccountUsage();

    if (location.pathname !== prevProps.location.pathname) {
      if (
        // opening file modal
        !(location.state &&
          location.state.modal &&
          (!prevProps.location.state ||
            !prevProps.location.state.modal)) &&
        // closing file modal
        !(prevProps.location.state &&
          prevProps.location.state.modal &&
          (!location.state ||
            !location.state.modal)) &&
        // file modal still open
        !(prevProps.location.state &&
          prevProps.location.state.modal &&
          location.state &&
          location.state.modal)
      ) {
        clearSelectedFiles();
        clearSelectedCollections();
      }

      if (
        (isArchiveUrl(location.pathname) && !isArchiveUrl(prevProps.location.pathname)) ||
        (!isArchiveUrl(location.pathname) && isArchiveUrl(prevProps.location.pathname))
      ) {
        if (!isArchive()) {
          loadFeaturedCollections();
        }
      }
    }

    if (!location.state || !location.state.modal) {
      this.previousLocation = location;
    }

    if (
      selectedFiles.length !== prevProps.selectedFiles.length &&
      ((selectedFiles.length === 0 && prevProps.selectedFiles.length !== 0) ||
        (selectedFiles.length !== 0 && prevProps.selectedFiles.length === 0))
    ) {
      resizeSplitContentContainer();
    }

    resizeAccountUsageInfo();
  }

  // eslint-disable-next-line class-methods-use-this
  componentWillUnmount() {
    window.removeEventListener('resize', resizeSplitContentContainer);
    window.removeEventListener('resize', resizeAccountUsageInfo);
  }

  checkAccountUsage() {
    const {
      history,
      location,
      profile,
    } = this.props;

    if (profile) {
      if (profile.client) {
        if (subscriptionExpired(profile.client)) {
          const path = userIsAdmin() ? '/admin/settings/subscription' : '/subscription';

          if (location.pathname !== path) {
            history.push(path);
          }
        }
      }

      if (!profile.g2fa_enabled && getSetting('2fa_required')) {
        if (
          location.pathname.replace(/\/$/, '') !== '/profile/edit' &&
          location.pathname.replace(/\/$/, '') !== '/subscription' &&
          location.pathname.replace(/\/$/, '') !== '/admin/settings/subscription'
        ) {
          history.push('/profile/edit/?tab=2fa');
        }
      }
    }
  }

  setSupportWidgetVisibility() {
    const {
      selectedFiles,
      selectedCollections,
      fileModalOpen,
      uploadModal,
    } = this.props;

    if (
      (
        (
          selectedFiles.length === 0 &&
          selectedCollections.length === 0
        ) ||
        fileModalOpen
      )
      && !uploadModal
      && !isImportModalOpen()
    ) {
      showIntercomWidget();
    } else {
      hideIntercomWidget();
    }
  }

  fileWithLocation() {
    return (
      <File originalLocation={this.previousLocation} />
    );
  }

  handleTrialMessage() {
    const {
      handleTrialModal,
      profile,
    } = this.props;

    if (
      (
        (profile?.client?.is_in_trial && getSetting('show_trial_welcome_message'))
        || getSetting('restart_trial_welcome_message')
      ) && userIsAdmin()
    ) {
      handleTrialModal();
    }
  }

  render() {
    const {
      profile,
      location,
      history,
      connectDropTarget,
      isOver,
    } = this.props;

    const isSecondSidebarOpen = shouldShowSecondSidebar(location);

    if (!profile) {
      return (
        <LoadingMain />
      );
    }

    const isModal = !!((
      (
        location.state &&
        location.state.modal &&
        this.previousLocation !== location
      ) ||
      location.pathname.match(`/files/(${uuidPattern})`) ||
      location.pathname.match(`/archive/files/(${uuidPattern})`) ||
      location.pathname.match(`/trash/files/(${uuidPattern})`)
    ));

    if (location.pathname === '/' && getSetting('home_page') && getSetting('home_page') !== 'dashboard') {
      if (getSetting('home_page') === 'all-collections') {
        history.push('/collections');
        return null;
      }
      if (getSetting('home_page') === 'all-files') {
        history.push('/files');
        return null;
      }
      return (
        <Page pageId={getSetting('home_page')} />
      );
    }

    const params = (new URL(document.location)).searchParams;
    if (!isLoggedIn()) {
      let loginUrl = '/login';
      loginUrl = `${loginUrl}?${params.toString()}`;
      history.push(loginUrl);
    } else if (params.get('login_token') || params.get('jwt_token')) {
      params.delete('login_token');
      params.delete('jwt_token');
      history.replace(`${location.pathname}?${params.toString()}`);
    }

    return connectDropTarget((
      <div>
        <AccountUsageInfo />
        <div className={`layout layout-nav-side${getSetting('cs1') ? ' cs1' : ''}`}>
          {(profile && userCan('file.upload') && !accountUsageLock(profile.client) && !subscriptionExpired(profile.client)) && (
            <UploadDropOverlay show={isOver} />
          )}
          <Sidebar />
          <div
            className={classnames('main-container', {
              'main-container-second-sidebar-open': isSecondSidebarOpen,
              'main-container-second-sidebar-closed': !isSecondSidebarOpen,
            })}
          >
            <Toolbar />
            <Switch location={isModal ? this.previousLocation : location}>
              <Redirect
                exact
                from="/archive"
                to="/archive/files"
              />
              <Redirect exact from="/collectiongroups" to="/collections" />
              <Route exact path="/" component={Dashboard} />
              <Route exact path="/dashboard" component={Dashboard} />
              <Route path="/results" component={SearchResults} />
              <Route path="/files/related/:relatedFileId" component={Files} />
              <Route path="/files/:fileId" component={Files} />
              <Route path="/files" component={Files} />
              <Route
                path="/archive/files/:fileId"
                render={() => (
                  !userHasFeature('archive') ? (
                    <Redirect
                      to={{
                        pathname: '/',
                        state: { upgradeModal: 'archive' },
                      }}
                    />
                  ) : <Files />
                )}
              />
              <Route
                path="/archive/files"
                render={() => (
                  !userHasFeature('archive') ? (
                    <Redirect
                      to={{
                        pathname: '/',
                        state: { upgradeModal: 'archive' },
                      }}
                    />
                  ) : <Files />
                )}
              />
              <Redirect
                exact
                from="/trash/files"
                to="/trash"
              />
              <Redirect
                exact
                from="/trash/collections"
                to="/trash"
              />
              <Route path={`/trash/collections/:collectionId(${uuidPattern})`} component={Files} />
              <Route path="/trash" component={Files} />
              <Route path="/favorites" component={Files} />
              <Route
                path="/people"
                render={() => (
                  !userHasFeature('face-recognition') ? (
                    <Redirect
                      to={{
                        pathname: '/',
                        state: { upgradeModal: 'face-recognition' },
                      }}
                    />
                  ) : <People />
                )}
              />
              <Route exact path="/collections/all" component={Collections} />
              {userHasCap('my_collections') && (
                <Route exact path="/collections/my" component={Collections} />
              )}
              <Route exact path={`/collections/:collectionId(${uuidPattern})`} component={Files} />
              <Route exact path="/uploads" component={Uploads} />
              <Route exact path={`/(groups|uploads|edits)/:groupId(${uuidPattern})`} component={Files} />
              <Route exact path="/downloads" component={Downloads} />
              <Route exact path={`/downloads/:downloadId(${uuidPattern})`} component={Files} />
              <Route exact path="/collections" component={Collections} />
              <Route
                exact
                path="/archive/collections/all"
                render={() => (
                  !userHasFeature('archive') ? (
                    <Redirect
                      to={{
                        pathname: '/',
                        state: { upgradeModal: 'archive' },
                      }}
                    />
                  ) : <Collections />
                )}
              />
              {userHasCap('my_collections') && (
                <Route
                  exact
                  path="/archive/collections/my"
                  render={() => (
                    !userHasFeature('archive') ? (
                      <Redirect
                        to={{
                          pathname: '/',
                          state: { upgradeModal: 'archive' },
                        }}
                      />
                    ) : <Collections />
                  )}
                />
              )}
              <Route
                exact
                path={`/archive/collections/:collectionId(${uuidPattern})`}
                render={() => (
                  !userHasFeature('archive') ? (
                    <Redirect
                      to={{
                        pathname: '/',
                        state: { upgradeModal: 'archive' },
                      }}
                    />
                  ) : <Files />
                )}
              />
              <Route
                exact
                path="/archive/collections"
                render={() => (
                  !userHasFeature('archive') ? (
                    <Redirect
                      to={{
                        pathname: '/',
                        state: { upgradeModal: 'archive' },
                      }}
                    />
                  ) : <Collections />
                )}
              />
              {getSetting('download_requests_enabled') && (
                <Route exact path="/download-requests" component={DownloadRequests} />
              )}
              {getSetting('download_requests_enabled') && (
                <Route exact path={`/download-requests/:downloadRequestId(${uuidPattern})`} component={DownloadRequestEdit} />
              )}
              <Redirect from="/shares" to="/share-links" />
              {userHasCap('share') && (
                <Route exact path="/share-links" component={Shares} />
              )}
              {userHasCap('share') && (
                <Route exact path={`/share-links/:shareUuid(${uuidPattern})/details`} component={ShareDetails} />
              )}
              {userCan('manage_settings') && (
                <Route exact path={`/admin/settings/:tab(teams)/:groupId(${uuidPattern})/edit`} component={AdminSettings} />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/teams/:subTab(add)"
                  to="/admin/settings/teams/:subTab(add)"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/teams"
                  to="/admin/settings/teams"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from={`/admin/teams/:groupId(${uuidPattern})/edit`}
                  to={`/admin/settings/teams/:groupId(${uuidPattern})/edit`}
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/roles/:subTab(add)"
                  to="/admin/settings/roles/:subTab(add)"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/roles/:roleId(\d+)/edit"
                  to="/admin/settings/roles/:roleId(\d+)/edit"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/roles/"
                  to="/admin/settings/roles/"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/roles/:roleId(\d+)"
                  to="/admin/settings/roles/:roleId(\d+)/edit"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from={`/admin/teams/:groupId(${uuidPattern})/edit`}
                  to={`/admin/settings/teams/:groupId(${uuidPattern})/edit`}
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from={`/admin/teams/:groupId(${uuidPattern})`}
                  to={`/admin/settings/teams/:groupId(${uuidPattern})/edit`}
                />
              )}
              {userCan('manage_settings') && (
                <Route exact path="/admin/settings/:tab(roles)/:roleId(\d+)/edit" component={AdminSettings} />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/roles/:roleId(\d+)"
                  to="/admin/settings/roles/:roleId(\d+)/edit"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/roles/:roleId(\d+)/edit"
                  to="/admin/settings/roles/:roleId(\d+)/edit"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/roles/"
                  to="/admin/settings/roles/"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/roles/:tab(add)"
                  to="/admin/settings/roles/:tab(add)"
                />
              )}
              {(userCan('add_users') || userCan('manage_settings')) && (
                <Redirect
                  from="/admin/users/"
                  to="/admin/settings/users/"
                />
              )}
              {(userCan('add_users') || userCan('manage_settings')) && (
                <Redirect
                  from="/admin/users/:subTab(add)"
                  to="/admin/settings/users/:subTab(add)"
                />
              )}
              {(userCan('add_users') || userCan('manage_settings')) && (
                <Route exact path={`/admin/settings/:tab(users)/:userUuid(${uuidPattern})/edit`} component={AdminSettings} />
              )}
              {(userCan('add_users') || userCan('manage_settings')) && (
                <Redirect
                  from={`/admin/users/:userId(${uuidPattern})`}
                  to={`/admin/users/:userId(${uuidPattern})/edit`}
                />
              )}
              {userCan('manage_settings') && (
                <Redirect from="/admin/collectiongroups" to="/admin/settings" />
              )}
              {userCan('manage_settings') && (
                <Redirect from="/admin/collectiongroupgroups" to="/admin/settings" />
              )}
              {userCan('manage_settings') && (
                <Route exact path="/admin/settings/:tab(categories)/:taxonomyId/edit" component={AdminSettings} />
              )}
              {userCan('manage_settings') && (
                <Route exact path="/admin/settings/:tab(custom-fields)/:customFieldId/edit" component={AdminSettings} />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/categories/:subTab(add)"
                  to="/admin/settings/categories/:subTab(add)"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/categories/:taxonomyId(\d+)/edit"
                  to="/admin/settings/categories/:taxonomyId(\d+)/edit"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/categories/:taxonomyId(\d+)"
                  to="/admin/settings/categories/:taxonomyId(\d+)/edit"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/statuses"
                  to="/admin/settings/file-statuses"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/sizes"
                  to="/admin/settings/file-sizes"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect from="/admin/analytics/:tab?" to="/admin/reports" />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/filters"
                  to="/admin/settings/filters"
                />
              )}
              {userCan('manage_settings') && (
                <Route exact path="/admin/settings/:tab(filters)" component={AdminSettings} />
              )}
              {userCan('manage_settings') && (
                <Route
                  exact
                  path="/admin/pages"
                  render={() => (<AdminPages />)}
                />
              )}
              {userCan('manage_settings') && (
                <Route
                  exact
                  path="/admin/pages/add"
                  render={() => (<AdminPageEdit />)}
                />
              )}
              {userCan('manage_settings') && (
                <Route
                  exact
                  path="/admin/pages/:pageId(\d+)/details"
                  component={AdminPageDetails}
                />
              )}
              {userCan('manage_settings') && (
                <Route
                  exact
                  path="/admin/pages/:pageId(\d+)/edit"
                  render={() => (<AdminPageEdit />)}
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/pages/:pageId(\d+)"
                  to="/admin/pages/:pageId(\d+)/edit"
                />
              )}
              {userCan('integrations') && (
                <Route exact path="/admin/settings/:tab(integrations)/:serviceId" component={AdminSettings} />
              )}
              {(userCan('add_users') || userCan('manage_settings')) && (
                <Route exact path="/admin/settings/:tab?/:subTab?" component={AdminSettings} />
              )}
              {(userCan('manage_settings') || userHasCap('reports')) && (
                <Route
                  exact
                  path="/admin/reports"
                  render={() => (
                    !userHasFeature('reports') ? (
                      <Redirect
                        to={{
                          pathname: '/',
                          state: { upgradeModal: 'reports' },
                        }}
                      />
                    ) : <AdminReports />
                  )}
                />
              )}
              {(userCan('manage_settings') || userHasCap('reports')) && (
                <Route
                  path="/admin/reports/:reportId"
                  render={() => (
                    !userHasFeature('reports') ? (
                      <Redirect
                        to={{
                          pathname: '/',
                          state: { upgradeModal: 'reports' },
                        }}
                      />
                    ) : <AdminReport />
                  )}
                />
              )}
              {(userCan('manage_settings')) && (
                <Redirect
                  from="/admin/export"
                  to="/admin/settings/export"
                />
              )}
              {userCan('manage_settings') && (
                <Redirect
                  from="/admin/workspace"
                  to="/admin/settings"
                />
              )}
              {!profile.client?.payment_disabled && (
                userIsAdmin() ? (
                  <Route exact path="/admin/settings/subscription" component={AdminSettings} />
                ) : (
                  <Route exact path="/subscription" component={AdminPaymentInfo} />
                )
              )}
              {!userIsAdmin() && (
                <Redirect
                  from="/admin/settings/subscription"
                  to="/subscription"
                />
              )}
              {userIsAdmin() && (
                <Redirect
                  from="/subscription"
                  to="/admin/settings/subscription"
                />
              )}
              <Route path={`/profile/:userUuid(${uuidPattern})`} component={UserProfile} />
              <Route exact path="/profile/edit" component={ProfileEdit} />
              <Redirect
                from="/profile"
                to="/profile/edit"
              />
              <Route exact path="/connect" component={Connect} />
              <Route exact path="/importers/:serviceId/oauth2" component={IntegrationAuthCallback} />
              <Route exact path="/integrations/:serviceId/oauth2" component={IntegrationAuthCallback} />
              {userCan('manage_settings') && (
                <Route
                  exact
                  path="/admin/duplicates"
                  render={() => (
                    !userHasFeature('duplicate-files') ? (
                      <Redirect
                        to={{
                          pathname: '/',
                          state: { upgradeModal: 'duplicate-files' },
                        }}
                      />
                    ) : <AdminDuplicates />
                  )}
                />
              )}
              {(userCan('manage_settings') || userHasCap('ftp')) && (
                <Route
                  exact
                  path="/admin/ftp-accounts"
                  render={() => (
                    !userHasFeature('ftp') ? (
                      <Redirect
                        to={{
                          pathname: '/',
                          state: { upgradeModal: 'ftp' },
                        }}
                      />
                    ) : <AdminFTPAccounts />
                  )}
                />
              )}
              {(userCan('manage_settings') || userHasCap('ftp')) && (
                <Route
                  exact
                  path="/admin/ftp-queue"
                  render={() => (
                    !userHasFeature('ftp') ? (
                      <Redirect
                        to={{
                          pathname: '/',
                          state: { upgradeModal: 'ftp' },
                        }}
                      />
                    ) : <AdminFTPQueue />
                  )}
                />
              )}
              {(userCan('manage_settings') || userHasCap('upload_pages')) && (
                <Route
                  exact
                  path="/admin/upload-pages"
                  render={() => (
                    !userHasFeature('upload-pages') ? (
                      <Redirect
                        to={{
                          pathname: '/',
                          state: { upgradeModal: 'upload-pages' },
                        }}
                      />
                    ) : <AdminUploadPages />
                  )}
                />
              )}
              {(userCan('manage_settings') || userHasCap('upload_pages')) && (
                <Route
                  exact
                  path="/admin/upload-pages/:uploadPageUuid"
                  render={() => (
                    !userHasFeature('upload-pages') ? (
                      <Redirect
                        to={{
                          pathname: '/',
                          state: { upgradeModal: 'upload-pages' },
                        }}
                      />
                    ) : <AdminUploadPageDetails />
                  )}
                />
              )}
              {(!userCan('manage_settings') && userCan('integrations')) && (
                <Route exact path="/integrations" component={Integrations} />
              )}
              {(!userCan('manage_settings') && userCan('integrations')) && (
                <Route exact path="/integrations/:serviceId" component={Integrations} />
              )}
              {userCan('desktop_sync') && (
                <Route exact path="/synced-collections" component={SyncedCollections} />
              )}
              <Route exact path="/content/:pageId/search" component={PageInternal} />
              <Route path="/content/:pageId" component={PageInternal} />
              <Route exact path="/notifications" component={Notifications} />
              <Route component={NoMatch} />
            </Switch>
            <Actionbar />
            <ConfirmationModal />
          </div>
          {isModal ? <Route path="/files/:fileId" component={this.fileWithLocation} /> : null}
          {isModal ? <Route path="/archive/files/:fileId" component={userHasFeature('archive') ? this.fileWithLocation : null} /> : null}
          {isModal ? <Route path="/trash/files/:fileId" component={this.fileWithLocation} /> : null}
          <FlashMessages />
          <EventHandler />
          <NavigationBlocker />
          <TermsModalHandler />
        </div>
      </div>
    ));
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  selectedFiles: state.Files.selectedFiles,
  selectedCollections: state.Collections.selectedCollections,
  fileModalOpen: state.Files.fileModalOpen,
  uploadModal: state.Files.uploadModal,
  secondSidebarOpen: state.App.secondSidebarOpen,
});

const mapDispatchToProps = dispatch => ({
  loadFeaturedCollections: () => {
    dispatch(fetchFeaturedCollections());
  },
  loadMetadataGroups: () => {
    dispatch(fetchMetadataGroups());
  },
  loadTaxonomies: () => {
    dispatch(fetchTaxonomies());
  },
  loadCustomFields: () => {
    dispatch(fetchCustomFields());
  },
  loadFileStatuses: () => {
    dispatch(fetchFileStatuses());
  },
  clearSelectedFiles: () => {
    dispatch(setSelectedFiles([]));
    dispatch(setSelectedAllFiles(false));
  },
  clearSelectedCollections: () => {
    dispatch(setSelectedCollections([]));
    dispatch(setSelectedAllCollections(false));
  },
  handleTrialModal: () => {
    dispatch(showModal('TrialModal', {
      className: 'modal-start-trial',
      messageOnly: true,
    }));
  },
});

AppContent.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  profile: PropTypes.object,
  selectedFiles: PropTypes.array.isRequired,
  selectedCollections: PropTypes.array.isRequired,
  fileModalOpen: PropTypes.bool.isRequired,
  uploadModal: PropTypes.bool.isRequired,
  loadFeaturedCollections: PropTypes.func.isRequired,
  loadTaxonomies: PropTypes.func.isRequired,
  loadMetadataGroups: PropTypes.func.isRequired,
  loadCustomFields: PropTypes.func.isRequired,
  loadFileStatuses: PropTypes.func.isRequired,
  clearSelectedFiles: PropTypes.func.isRequired,
  clearSelectedCollections: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  isOver: PropTypes.bool.isRequired,
  handleTrialModal: PropTypes.func.isRequired,
};

AppContent.defaultProps = {
  profile: null,
};

export default flow(DropTarget(NativeTypes.FILE, uploadFilesTarget, (DNDconnect, monitor) => ({
  connectDropTarget: DNDconnect.dropTarget(),
  isOver: monitor.isOver({ shallow: true }),
})), connect())(
  connect(mapStateToProps, mapDispatchToProps)(AppContent),
);
